import React, { useContext, useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { MadLibContext } from '@glowforge/gf-styled/components/mad-lib-email';
import { stringSubstitution } from '@glowforge/gf-styled/components/contentful-views/util';
import LayeredDialog from '@glowforge/gf-styled/components/dialog/layered-dialog';
import { SVG } from '@glowforge/gf-styled/components';
import {
  Header4,
  Paragraph,
} from '@glowforge/gf-styled/components/typography/Typography';
import ReactModal from 'react-modal';

import { fetchProducts } from '../../api/storefront';
import ReferralInfoContext from '../../context/ReferralInfoContext';
import ModalPriorityContext from '../../context/ModalPriorityContext/ModalPriorityContext';
import { useLocalStorage } from '../../hooks';
import { REACT_ROOT } from '../../pages/_app';
import useFeatureFlags from '../../hooks/use-feature-flags';
import LocationContext from '../../context/LocationContext';
import { CountryCodes } from '../../constants';

import {
  HeaderWrapper,
  CloseButton,
  ReferralHeader,
  UnderlineHeading,
  SubHeaderWrapper,
  ReferralPortrait,
  BodyWrapper,
} from './referral-modal.styles';
import CTAButton from './referral-apply-btn';

/**
 * Unique ID for this modal
 */
const MODAL_ID = 'referral';

/**
 * Use localstorage to keep track of referral code changes
 */
const LAST_REFERRAL_CODE_KEY = '_gf_last_referral_code';
const DISMISSED_KEY = '_gf_referral_modal_dismissed';

/**
 * If the user is being redirected to the site after clicking on a referral
 * link, show them a modal that makes it very obvious that the referral discount
 * is being applied
 */
const ReferralModal = ({ background, cta, strings, markdownStrings }) => {
  const featureFlags = useFeatureFlags();
  const internationalSales = featureFlags['space-dogs'];
  const { country } = useContext(LocationContext);

  const animationTimeout = useRef();

  const { referralInfo, ensureReferralInfo } = useContext(ReferralInfoContext);

  const { isModalOpen, openModal, closeModal } =
    useContext(ModalPriorityContext);

  const [, hideMadLib] = useContext(MadLibContext);

  const [products, setProducts] = useState({});

  const [dismissed, setDismissed] = useLocalStorage(DISMISSED_KEY, false);

  const [lastReferralCode, setLastReferralCode] = useLocalStorage(
    LAST_REFERRAL_CODE_KEY,
    ''
  );

  const isOpen = isModalOpen(MODAL_ID);

  const {
    code = '',
    referrerName = '',
    photoUrl = '',
    pl: { id = '' } = {},
  } = referralInfo || {};

  /**
   * Load referral info when component mounts. When component unmounts, clean up
   * any animation timers that might be running
   */
  useEffect(() => {
    ensureReferralInfo();
    return () => {
      clearTimeout(animationTimeout.current);
      closeModal(MODAL_ID);
    };
  }, []);

  /**
   * Fetch products if necessary
   */
  useEffect(() => {
    if (Object.keys(referralInfo || {}).length < 1) {
      fetchProducts().then((result) => setProducts(result));
    }
  }, [referralInfo]);

  // Referral code takes precedence over promotional link
  const referralCode = code || id;

  const {
    pro: { referral: { discount: proString = '0' } = {} } = {},
    plus: { referral: { discount: plusString = '0' } = {} } = {},
    basic: { referral: { discount: basicString = '0' } = {} } = {},
  } = products || {};

  const proDiscount = Math.abs(parseInt(proString, 10));
  const plusDiscount = Math.abs(parseInt(plusString, 10));
  const basicDiscount = Math.abs(parseInt(basicString, 10));

  /**
   * Show referral modal if it has not been dismissed, or they have a new
   * referral code
   */
  useEffect(() => {
    if (
      !isOpen &&
      Object.keys(products).length > 0 &&
      referralCode &&
      (!dismissed || referralCode !== lastReferralCode)
    ) {
      openModal(MODAL_ID, 10);
      setDismissed(false);
      setLastReferralCode(referralCode);
      hideMadLib();
    }
  }, [products, referralCode, lastReferralCode, isOpen, dismissed]);

  /**
   * Handle clicks on the close button
   */
  const close = () => {
    closeModal(MODAL_ID);
    setDismissed(true);
  };

  const stringTemplateData = {
    name: referrerName,
    proDiscount,
    plusDiscount,
    basicDiscount,
  };

  const countrySuffix =
    internationalSales && country === CountryCodes.GB ? '-gb' : '';

  const isAffiliate = referralInfo?.isAffiliate ? '-affiliate' : '';

  const headerUnderline = stringSubstitution(
    strings[`referral-modal-rebrand-header-underline${countrySuffix}`] || '',
    stringTemplateData
  );
  const header = stringSubstitution(
    strings[`referral-modal-rebrand-header${countrySuffix}`] || '',
    stringTemplateData
  );

  const subheadStringId = `referral-modal-rebrand-subhead${isAffiliate}${countrySuffix}`;

  const subhead = stringSubstitution(
    markdownStrings[subheadStringId] || '',
    stringTemplateData
  );

  const bodyStringId = `referral-modal-rebrand-body${isAffiliate}${countrySuffix}`;

  const closeText = strings['referral-modal-close-text'];

  const body = stringSubstitution(
    markdownStrings[bodyStringId] || '',
    stringTemplateData
  );

  return (
    <LayeredDialog
      appElement={REACT_ROOT}
      isOpen={isOpen}
      // eslint-disable-next-line react/jsx-no-bind
      onRequestClose={close}
      offsetHorizontal={200}
      offsetVertical={60}
      modalStyles={{
        content: {
          top: '0',
          left: '0',
          right: '0',
          bottom: '0',
          background: 'none',
        },
        overlay: {
          ...ReactModal.defaultStyles.overlay,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 10000,
        },
      }}
      {...background}>
      <HeaderWrapper>
        <ReferralHeader>
          {headerUnderline ? (
            <UnderlineHeading as='span'>{headerUnderline}</UnderlineHeading>
          ) : null}
          {header ? <Header4 as='span'>{header}</Header4> : null}
        </ReferralHeader>
        <CloseButton
          aria-label='Close Dialog'
          variant='ghost-blue'
          onClick={close}
          icon={<SVG width={12} height={12} id='close' />}>
          {closeText}
        </CloseButton>
      </HeaderWrapper>
      <SubHeaderWrapper>
        <ReferralPortrait src={photoUrl} />
        <Paragraph>{subhead}</Paragraph>
      </SubHeaderWrapper>
      <BodyWrapper source={body} />

      {cta?.fields ? (
        <CTAButton setDismissed={setDismissed} {...cta.fields} />
      ) : null}
    </LayeredDialog>
  );
};

ReferralModal.propTypes = {
  background: PropTypes.shape({
    backgroundImage: PropTypes.string,
    backgroundPattern: PropTypes.string,
  }),
  cta: PropTypes.shape({
    fields: PropTypes.shape({
      type: PropTypes.string,
      icon: PropTypes.shape({
        fields: PropTypes.shape({
          id: PropTypes.string,
        }),
      }),
      text: PropTypes.string,
      url: PropTypes.string,
    }),
  }),
  strings: PropTypes.shape({
    'referral-modal-rebrand-header-underline': PropTypes.string,
    'referral-modal-rebrand-header': PropTypes.string,
    'referral-modal-close-text': PropTypes.string,
  }),
  markdownStrings: PropTypes.shape({}),
};

ReferralModal.defaultProps = {
  background: {},
  cta: null,
  strings: {},
  markdownStrings: {},
};

export default ReferralModal;
